'use client';
// Client-Modul: Kontext und Hooks laufen nur im Browser bzw. im SSR-Rahmen der
// Seite. Server-Code (Routen, Strukturdaten) importiert reine Helfer aus Modulen,
// die auch diese Hooks anbieten (`shared/anzahl.ts`) — mit der Markierung ist
// das erlaubt, die Hooks bleiben dort Verweise (SEO-Paket F7, Abbildungen).
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { i18nAPI } from '../services/api';
import { COUNTRY_COOKIE, DEFAULT_LANGUAGE, DEFAULT_LANGUAGES, HEIMATLAND, LANGUAGE_COOKIE } from '../utils/standardSprachen';
import { istGlobalerPfad } from '../utils/globalePfade';

// =============================================================================
// Konfiguration - IKEA-Style URLs: /{country}/{language}/...
// Statische Defaults als Fallback, wird dynamisch aus API überschrieben
// =============================================================================

// Statische Defaults für das ROUTING (Fallback falls API nicht erreichbar).
//
// Sie entscheiden, welche Adresse ein gültiges Präfix ist, und sonst nichts.
// Ob Anmeldung oder Registrierung in einem Land offen ist, steht NICHT hier und
// darf hier nie stehen: `show_login`/`show_register` sind Compliance-Schalter
// (`backend/app/compliance/market_release.py`), und ihre einzige Quelle ist
// `/api/i18n/locale-config`. Bis 15.09.2026 prüfte die Anmeldeseite
// `currentCountry?.showRegister !== false` — diese Liste trägt das Feld nicht,
// also stand die Registrierung offen, solange die Antwort fehlte oder
// scheiterte, auch für die gesperrten Märkte AR, AU, BR und MX. Gelesen wird die
// Freigabe über `anmeldeFreigabe` weiter unten, nie über `currentCountry`.

const DEFAULT_COUNTRIES = {
  de: { code: 'de', nameNative: 'Deutschland', nameEnglish: 'Germany', flag: '🇩🇪', languages: ['de'], seoLanguages: ['de'], defaultLanguage: 'de', currency: 'EUR' },
  at: { code: 'at', nameNative: 'Österreich', nameEnglish: 'Austria', flag: '🇦🇹', languages: ['de'], seoLanguages: ['de'], defaultLanguage: 'de', currency: 'EUR' },
  ch: { code: 'ch', nameNative: 'Schweiz', nameEnglish: 'Switzerland', flag: '🇨🇭', languages: ['de', 'fr', 'it'], seoLanguages: ['de', 'fr'], defaultLanguage: 'de', currency: 'CHF' },
  gb: { code: 'gb', nameNative: 'United Kingdom', nameEnglish: 'United Kingdom', flag: '🇬🇧', languages: ['en'], seoLanguages: ['en'], defaultLanguage: 'en', currency: 'GBP' },
  us: { code: 'us', nameNative: 'United States', nameEnglish: 'United States', flag: '🇺🇸', languages: ['en'], seoLanguages: [], defaultLanguage: 'en', currency: 'USD' },
  fr: { code: 'fr', nameNative: 'France', nameEnglish: 'France', flag: '🇫🇷', languages: ['fr'], seoLanguages: ['fr'], defaultLanguage: 'fr', currency: 'EUR' },
  it: { code: 'it', nameNative: 'Italia', nameEnglish: 'Italy', flag: '🇮🇹', languages: ['it'], seoLanguages: [], defaultLanguage: 'it', currency: 'EUR' },
  es: { code: 'es', nameNative: 'España', nameEnglish: 'Spain', flag: '🇪🇸', languages: ['es'], seoLanguages: [], defaultLanguage: 'es', currency: 'EUR' },
  nl: { code: 'nl', nameNative: 'Nederland', nameEnglish: 'Netherlands', flag: '🇳🇱', languages: ['nl'], seoLanguages: [], defaultLanguage: 'nl', currency: 'EUR' },
  pl: { code: 'pl', nameNative: 'Polska', nameEnglish: 'Poland', flag: '🇵🇱', languages: ['pl'], seoLanguages: [], defaultLanguage: 'pl', currency: 'PLN' },
  se: { code: 'se', nameNative: 'Sverige', nameEnglish: 'Sweden', flag: '🇸🇪', languages: ['sv'], seoLanguages: [], defaultLanguage: 'sv', currency: 'SEK' },
  be: { code: 'be', nameNative: 'België', nameEnglish: 'Belgium', flag: '🇧🇪', languages: ['nl', 'fr', 'de'], seoLanguages: [], defaultLanguage: 'nl', currency: 'EUR' },
  ca: { code: 'ca', nameNative: 'Canada', nameEnglish: 'Canada', flag: '🇨🇦', languages: ['en', 'fr'], seoLanguages: [], defaultLanguage: 'en', currency: 'CAD' },
  au: { code: 'au', nameNative: 'Australia', nameEnglish: 'Australia', flag: '🇦🇺', languages: ['en'], seoLanguages: [], defaultLanguage: 'en', currency: 'AUD' },
  ie: { code: 'ie', nameNative: 'Ireland', nameEnglish: 'Ireland', flag: '🇮🇪', languages: ['en'], seoLanguages: [], defaultLanguage: 'en', currency: 'EUR' },
  mx: { code: 'mx', nameNative: 'México', nameEnglish: 'Mexico', flag: '🇲🇽', languages: ['es'], seoLanguages: [], defaultLanguage: 'es', currency: 'MXN' },
  ar: { code: 'ar', nameNative: 'Argentina', nameEnglish: 'Argentina', flag: '🇦🇷', languages: ['es'], seoLanguages: [], defaultLanguage: 'es', currency: 'ARS' },
  dk: { code: 'dk', nameNative: 'Danmark', nameEnglish: 'Denmark', flag: '🇩🇰', languages: ['da'], seoLanguages: [], defaultLanguage: 'da', currency: 'DKK' },
  no: { code: 'no', nameNative: 'Norge', nameEnglish: 'Norway', flag: '🇳🇴', languages: ['no'], seoLanguages: [], defaultLanguage: 'no', currency: 'NOK' },
  pt: { code: 'pt', nameNative: 'Portugal', nameEnglish: 'Portugal', flag: '🇵🇹', languages: ['pt'], seoLanguages: [], defaultLanguage: 'pt', currency: 'EUR' },
  br: { code: 'br', nameNative: 'Brasil', nameEnglish: 'Brazil', flag: '🇧🇷', languages: ['pt'], seoLanguages: [], defaultLanguage: 'pt', currency: 'BRL' },
};

// Dynamisch geladene Config (wird beim App-Start aus API geladen)
let dynamicLanguages = null;
let dynamicCountries = null;
let dynamicHreflangAlternates = null;
// Vorgabe und Stand der Matrix aus derselben Antwort — nur zusammen mit den
// drei Listen ergibt das die Form `LocaleConfig` des Fundament-Bausteins
// (`shared/localeConfig.ts`), die `seoKopf`/`sprachFassungen` erwarten.
let dynamicVorgaben = null;
let configLoaded = false;
let configLoadPromise = null;
// Die Länder aus der LETZTEN ERFOLGREICHEN Antwort dieser Sitzung — nicht aus
// dem localStorage-Cache und nicht aus DEFAULT_COUNTRIES. Nur sie tragen die
// Freigabe-Schalter. Der Cache bleibt fürs Routing (schnellerer erster Render),
// entscheidet aber nicht über eine Registrierung: sonst wäre er der Rückfall,
// der eine gescheiterte Antwort verdeckt.
let serverLaender = null;
let configFehler = null;

// Getter für aktuelle Config (dynamisch oder Fallback)
export const SUPPORTED_LANGUAGES = new Proxy({}, {
  get: (target, prop) => (dynamicLanguages || DEFAULT_LANGUAGES)[prop],
  ownKeys: () => Object.keys(dynamicLanguages || DEFAULT_LANGUAGES),
  getOwnPropertyDescriptor: (target, prop) => ({
    enumerable: true,
    configurable: true,
    value: (dynamicLanguages || DEFAULT_LANGUAGES)[prop]
  }),
  has: (target, prop) => prop in (dynamicLanguages || DEFAULT_LANGUAGES),
});

export const SUPPORTED_COUNTRIES = new Proxy({}, {
  get: (target, prop) => (dynamicCountries || DEFAULT_COUNTRIES)[prop],
  ownKeys: () => Object.keys(dynamicCountries || DEFAULT_COUNTRIES),
  getOwnPropertyDescriptor: (target, prop) => ({
    enumerable: true,
    configurable: true,
    value: (dynamicCountries || DEFAULT_COUNTRIES)[prop]
  }),
  has: (target, prop) => prop in (dynamicCountries || DEFAULT_COUNTRIES),
});

// Seit `20260915v_vorgaben_in_der_db` sind `show_login`/`show_register`
// NOT NULL. Ein Land ohne echten Boolean ist ein Fehler der Antwort, kein
// „offen" und kein „zu".
function laenderOhneSchalter(countries) {
  return Object.entries(countries)
    .filter(([, land]) => typeof land.showLogin !== 'boolean' || typeof land.showRegister !== 'boolean')
    .map(([code]) => code);
}

// Funktion um Config aus API zu laden
export async function loadLocaleConfig() {
  if (configLoaded) return { languages: dynamicLanguages, countries: dynamicCountries, hreflangAlternates: dynamicHreflangAlternates };
  if (configLoadPromise) return configLoadPromise;
  
  configLoadPromise = (async () => {
    // Ein neuer Versuch ist kein Fehler mehr, sondern ein laufender Abruf.
    configFehler = null;
    try {
      const response = await i18nAPI.getLocaleConfig();
      const data = response.data;
      
      if (data.languages && Object.keys(data.languages).length > 0) {
        dynamicLanguages = data.languages;
      }
      if (!data.countries || Object.keys(data.countries).length === 0) {
        // Ohne ein einziges Land gibt es nichts, woran eine Freigabe hängen
        // könnte — das ist ein Fehler der Antwort, kein leerer Normalzustand.
        throw new Error('locale-config ohne Länder');
      }
      const ohneSchalter = laenderOhneSchalter(data.countries);
      if (ohneSchalter.length > 0) {
        throw new Error(`locale-config ohne Freigabe-Schalter für: ${ohneSchalter.join(', ')}`);
      }
      dynamicCountries = data.countries;
      serverLaender = data.countries;
      if (data.hreflangAlternates) {
        dynamicHreflangAlternates = data.hreflangAlternates;
      }
      dynamicVorgaben = vorgabenAus(data);
      
      configLoaded = true;
      configFehler = null;

      // Im localStorage cachen für schnelleres Laden beim nächsten Besuch
      try {
        localStorage.setItem('localeConfig', JSON.stringify({
          languages: dynamicLanguages,
          countries: dynamicCountries,
          hreflangAlternates: dynamicHreflangAlternates,
          defaultCountry: data.defaultCountry,
          defaultLanguage: data.defaultLanguage,
          configVersion: data.configVersion,
          timestamp: Date.now()
        }));
      } catch (e) {
        // localStorage nicht verfügbar
      }
      
      return { languages: dynamicLanguages, countries: dynamicCountries, hreflangAlternates: dynamicHreflangAlternates };
    } catch (error) {
      // Laut, nicht als Warnung: Ohne diese Antwort sind Anmeldung und
      // Registrierung gesperrt (Regel #7). Das Routing läuft mit den statischen
      // Ländern weiter. `configLoaded` bleibt false und das Promise wird
      // verworfen, damit „Erneut versuchen" wirklich neu fragt.
      console.error('Locale-Config konnte nicht geladen werden — Anmeldung und Registrierung bleiben gesperrt:', error);
      configFehler = error;
      configLoadPromise = null;
      return { languages: DEFAULT_LANGUAGES, countries: DEFAULT_COUNTRIES, hreflangAlternates: null };
    }
  })();
  
  return configLoadPromise;
}

// Versuche beim Laden aus localStorage zu initialisieren
try {
  const cached = localStorage.getItem('localeConfig');
  if (cached) {
    const data = JSON.parse(cached);
    // Cache nur verwenden wenn weniger als 5 Minuten alt (war 1 Stunde)
    if (data.timestamp && Date.now() - data.timestamp < 300000) {
      if (data.languages) dynamicLanguages = data.languages;
      if (data.countries) dynamicCountries = data.countries;
      if (data.hreflangAlternates) dynamicHreflangAlternates = data.hreflangAlternates;
      // Ein Cache von vor dem 14.10.2026 trägt keine Vorgabe — dann fehlt die
      // Matrix (`getSprachMatrix`), bis die Antwort da ist.
      dynamicVorgaben = vorgabenAus(data);
    } else {
      // Cache abgelaufen - löschen
      localStorage.removeItem('localeConfig');
    }
  }
} catch (e) {
  // localStorage nicht verfügbar
}

/**
 * Legt die Sprachkonfiguration vor, die eine Server-Seite (Next.js-Phase 2)
 * mitbringt — auf dem Server UND im Browser, vor dem ersten Rendern.
 *
 * Ohne das rendert der Server mit `DEFAULT_COUNTRIES`, der Browser dagegen mit
 * dem localStorage-Cache von oben: zwei verschiedene Länderlisten, und React
 * verwirft beim Übernehmen das Server-HTML. Gesetzt werden nur die Listen fürs
 * Rendern — NICHT `serverLaender`: Die Anmelde-Freigabe kommt weiter nur aus der
 * Antwort, die diese Sitzung selbst geholt hat (`loadLocaleConfig`).
 */
export function legeLocaleConfigVor(config) {
  if (config.languages && Object.keys(config.languages).length > 0) dynamicLanguages = config.languages;
  if (config.countries && Object.keys(config.countries).length > 0) dynamicCountries = config.countries;
  if (config.hreflangAlternates) dynamicHreflangAlternates = config.hreflangAlternates;
  dynamicVorgaben = vorgabenAus(config);
}

/**
 * Legt die Freigabe-Schalter vor, die eine Server-Seite (Next.js-Phase 2) beim
 * Backend geholt hat — damit „Kostenlos starten“ schon im Server-HTML steht.
 *
 * Bis 09.10.2026 fehlte der Knopf dort auf jeder Produktseite: `serverLaender`
 * war auf dem Server immer leer, die Freigabe stand also auf `laedt`, und wer
 * die Seite ohne JavaScript las (Crawler, Link-Vorschau, ein Content-Audit),
 * fand als einzige Handlung „Preise ansehen“.
 *
 * Das ist eine ERFOLGREICHE Antwort des Backends, kein Cache und keine
 * statische Liste — dieselbe Prüfung wie im Lader (`laenderOhneSchalter`). Sie
 * kann bis zu fünf Minuten alt sein (`localeConfig` in `app/_seo/lokal.ts`);
 * im Browser löst sie die eigene Antwort dieser Sitzung ab, sobald
 * `loadLocaleConfig` sie hat, und registriert wird ohnehin nur, wenn das
 * Backend alle drei Schalter prüft (`services/registrierungs_freigabe.py`).
 *
 * Auf dem Server gilt immer die Antwort der Anfrage (das Modul lebt dort über
 * viele Anfragen); im Browser nur, solange die eigene noch fehlt — sonst
 * schriebe jedes neue Rendern der Seite die ältere Server-Antwort zurück.
 */
export function legeAnmeldeFreigabeVor(countries) {
  const imBrowser = typeof window !== 'undefined';
  if (imBrowser && (serverLaender || configFehler)) return;
  if (!countries || Object.keys(countries).length === 0) {
    console.error('Server-Seite ohne Länder — Anmeldung und Registrierung bleiben gesperrt');
    return;
  }
  const ohneSchalter = laenderOhneSchalter(countries);
  if (ohneSchalter.length > 0) {
    console.error(`Server-Seite ohne Freigabe-Schalter für: ${ohneSchalter.join(', ')}`);
    return;
  }
  serverLaender = countries;
}

// Funktion um Cache manuell zu invalidieren (für Admin)
export function invalidateLocaleCache() {
  try {
    localStorage.removeItem('localeConfig');
    configLoaded = false;
    configLoadPromise = null;
    dynamicLanguages = null;
    dynamicCountries = null;
    dynamicHreflangAlternates = null;
    dynamicVorgaben = null;
    serverLaender = null;
    configFehler = null;
  } catch (e) {
    // localStorage nicht verfügbar
  }
}

/**
 * Freigabe-Schalter eines Landes aus der letzten erfolgreichen Antwort.
 * Ein Land, das der Server nicht (mehr) anbietet, gilt als `laedt`: Der
 * Provider stellt dann auf das Vorgabeland um. Fehlt auch das, ist es ein Fehler.
 */
function bestimmeAnmeldeFreigabe(country) {
  const gesperrt = (stand) => ({ stand, login: false, registrierung: false });
  if (configFehler) return gesperrt('fehler');
  if (!serverLaender) return gesperrt('laedt');
  const land = serverLaender[country];
  if (!land) return gesperrt(serverLaender[DEFAULT_COUNTRY] ? 'laedt' : 'fehler');
  return { stand: 'bereit', login: land.showLogin, registrierung: land.showRegister };
}

/** Vorgabe und Stand einer Antwort von `/api/i18n/locale-config` — oder `null`, wenn sie fehlen. */
function vorgabenAus(daten) {
  if (!daten?.defaultCountry || !daten?.defaultLanguage) return null;
  return { defaultCountry: daten.defaultCountry, defaultLanguage: daten.defaultLanguage, configVersion: daten.configVersion };
}

/**
 * Die Matrix in der Form `LocaleConfig` des Fundament-Bausteins — für
 * `seoKopf` (hreflang, canonical) und `sprachFassungen` (Sprach-Links).
 *
 * Nur aus einer Antwort des Backends (geladen, von einer Server-Seite
 * vorgelegt oder frisch im Cache), NIE aus den statischen Listen oben: Die
 * kennen die SEO-Fassungen nicht, und eine Seite mit erfundenen hreflang wäre
 * schlimmer als eine ohne (Regel #7). Fehlt sie, ist das Ergebnis `null` —
 * wer sie braucht, rendert dann nichts und wird beim Laden neu gezeichnet.
 */
export function getSprachMatrix() {
  if (!dynamicLanguages || !dynamicCountries || !dynamicHreflangAlternates || !dynamicVorgaben) return null;
  return {
    languages: dynamicLanguages,
    countries: dynamicCountries,
    hreflangAlternates: dynamicHreflangAlternates,
    ...dynamicVorgaben,
  };
}

// Dynamische Getter für Mappings
function getUrlPrefixToLang() {
  const langs = dynamicLanguages || DEFAULT_LANGUAGES;
  return Object.fromEntries(
    Object.values(langs).map(lang => [lang.urlPrefix.toLowerCase(), lang.code])
  );
}

function getLangToUrlPrefix() {
  const langs = dynamicLanguages || DEFAULT_LANGUAGES;
  return Object.fromEntries(
    Object.values(langs).map(lang => [lang.code, lang.urlPrefix])
  );
}

// Für Abwärtskompatibilität (wird beim Zugriff aktualisiert)
export const URL_PREFIX_TO_LANG = new Proxy({}, {
  get: (target, prop) => getUrlPrefixToLang()[prop],
  has: (target, prop) => prop in getUrlPrefixToLang(),
});

const LANG_TO_URL_PREFIX = new Proxy({}, {
  get: (target, prop) => getLangToUrlPrefix()[prop],
  has: (target, prop) => prop in getLangToUrlPrefix(),
});

// Defaults
const DEFAULT_COUNTRY = 'de';

/**
 * Das Land, unter dem eine Sprache als Präfix gültig ist: das des Besuchers,
 * wenn es die Sprache führt — sonst das Heimatland der Sprache.
 *
 * Ein Präfix ist nur gültig, wenn das Land die Sprache anbietet (`/de/english`
 * gibt es nicht). Kommt jemand mit `?lang=en` aus einer Mail und hat ein
 * deutsches Cookie, gewinnt die Sprache der Mail; das Land weicht dann aufs
 * Heimatland der Sprache aus, statt die Sprache zu verlieren. Ein Schweizer
 * mit `?lang=fr` bleibt in der Schweiz (`/ch/francais`).
 */
export function landFuerSprache(country, language) {
  const land = SUPPORTED_COUNTRIES[country];
  if (land && land.languages.includes(language)) return country;
  return HEIMATLAND[language] || DEFAULT_COUNTRY;
}

// =============================================================================
// Cookie-Hilfsfunktionen
// =============================================================================

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const setCookie = (name, value, days = 365) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
};

// =============================================================================
// URL Parsing - IKEA Style: /{country}/{language}/...
// =============================================================================

/**
 * Parst Country und Language aus einem Pfad
 * z.B. "/ch/francais/leaderboard" → { country: "ch", language: "fr", rest: "/leaderboard" }
 */
// Der Kopf, mit dem die Adresse ans Backend reist — Gegenstueck zu
// LOCALE_HEADER in backend/app/utils/locale_resolver.py. Die Anfrage geht an
// /api/... und traegt kein Praefix; ohne diesen Kopf antwortet das Backend aus
// dem Cookie, und auf /fr/francais/... kam so eine deutsche Meldung unter einer
// franzoesischen Seite (gemessen 18.09.2026, 121apps-foundation#140).
export const LOCALE_HEADER = 'X-Locale';

// Der Query-Parameter, mit dem ein Link aus einer Mail seine Sprache mitbringt
// — dieselbe Semantik wie `LOCALE_QUERY`/`languageFromQuery` im Fundament
// (packages/ui/localeConfig.ts) und Gegenstueck zu `SPRACH_PARAMETER` in
// packages/core-py/mail_link.py (hier: backend/app/utils/fleet/mail_link.py).
// Mail-Links sind flach (`/reset-password?token=…&lang=it`): Viele Zielseiten
// gibt es nur ohne Praefix, und das Land des Empfaengers kennt die Mail nicht.
// Die Sprache kennt sie genau — die, in der sie geschrieben ist.
export const LOCALE_QUERY = 'lang';

/** `?lang=it` → `'it'`, wenn die App die Sprache fuehrt; sonst null. */
export function languageFromQuery(search = '') {
  const wert = new URLSearchParams(search).get(LOCALE_QUERY);
  if (!wert) return null;
  const code = wert.toLowerCase();
  return SUPPORTED_LANGUAGES[code] ? code : null;
}

/**
 * Der Wert fuer `X-Locale` aus der Adresse — null, wenn sie nichts sagt.
 *
 * Das Praefix gewinnt (`/fr/francais/…` → `fr-FR`); auf flachen Seiten zaehlt
 * der Parameter eines Mail-Links (`?lang=fr` → `fr`). Ohne `search` ginge die
 * Sprache einer Mail verloren: `/reset-password` war am 29.09.2026 auf Prod
 * immer deutsch, obwohl die Mail italienisch war.
 */
export function localeHeaderValue(pathname, search = '') {
  const parsed = parseLocaleFromPath(pathname);
  if (parsed.isValid && parsed.language) {
    return parsed.country ? `${parsed.language}-${parsed.country.toUpperCase()}` : parsed.language;
  }
  return languageFromQuery(search);
}

/** Kopfzeilen fuer einen API-Aufruf — gehoert in den Client, nicht an Aufrufe. */
export function localeHeaders(pathname, search = '') {
  const wert = localeHeaderValue(pathname, search);
  return wert ? { [LOCALE_HEADER]: wert } : {};
}

export function parseLocaleFromPath(pathname) {
  const parts = pathname.split('/').filter(Boolean);
  
  if (parts.length >= 2) {
    const countryPart = parts[0].toLowerCase();
    const langPart = parts[1].toLowerCase();
    
    // Ist es ein gültiges Land?
    if (SUPPORTED_COUNTRIES[countryPart]) {
      // Ist es eine gültige Sprache (URL-Prefix)?
      const langCode = URL_PREFIX_TO_LANG[langPart];
      if (langCode) {
        // Ist diese Sprache für dieses Land verfügbar?
        const country = SUPPORTED_COUNTRIES[countryPart];
        if (country.languages.includes(langCode)) {
          return {
            country: countryPart,
            language: langCode,
            languagePrefix: langPart,
            rest: '/' + parts.slice(2).join('/'),
            isValid: true
          };
        }
      }
    }
  }
  
  return {
    country: null,
    language: null,
    languagePrefix: null,
    rest: pathname,
    isValid: false
  };
}

/**
 * Lokalisiert eine Anfrage OHNE gültiges Sprach-Präfix — und behält dabei den
 * angefragten Pfad.
 *
 * Genau der ging bis zum 12.09.2026 verloren: Die Auffang-Route warf ihn weg
 * und schickte JEDEN unlokalisierten Einstieg auf die Preisseite
 * (/community-backtests → /de/deutsch/pricing). Betroffen waren die URLs aus
 * Mails, Widgets und geteilten Links — also die, die niemand selbst tippt.
 *
 * Ein angefangenes Präfix ist kein Seitenpfad: `/de` ist das Land, `/deutsch/…`
 * das alte Ein-Segment-Format. Beide werden zum Präfix, nicht zum Ziel. Der
 * Rest bleibt, samt Query und Anker.
 *
 * Bewusst EINE Funktion statt einer Route je Pfad: Eine Liste bekannter Pfade
 * altert (sie kannte `/tables`, aber nicht `/wertpapiere`), diese Regel nicht.
 */
export function lokalisiereAnfrage({ pathname = '/', search = '', hash = '' }, { country, language }) {
  const teile = pathname.split('/').filter(Boolean);

  let land = country;
  if (teile.length && SUPPORTED_COUNTRIES[teile[0].toLowerCase()]) {
    land = teile.shift().toLowerCase();
  }

  // Ein Mail-Link bringt seine Sprache als `?lang=` mit — sie schlaegt die des
  // Cookies wie ein Praefix. Die Query bleibt stehen (Token, Reiter, …).
  let sprache = languageFromQuery(search) || language;
  if (teile.length && URL_PREFIX_TO_LANG[teile[0].toLowerCase()]) {
    sprache = URL_PREFIX_TO_LANG[teile.shift().toLowerCase()];
  }

  return buildLocalizedPath(landFuerSprache(land, sprache), sprache, '/' + teile.join('/')) + search + hash;
}

/**
 * Prüft ob ein Pfad ein gültiges Locale-Prefix hat
 */
export function hasLocalePrefix(pathname) {
  return parseLocaleFromPath(pathname).isValid;
}

/**
 * Baut eine lokalisierte URL: /{country}/{language}/{path}
 */
export function buildLocalizedPath(country, language, path) {
  const langPrefix = LANG_TO_URL_PREFIX[language] || 'deutsch';
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `/${country}/${langPrefix}${cleanPath ? '/' + cleanPath : ''}`;
}

// =============================================================================
// Context
// =============================================================================

const LanguageContext = createContext(null);

/**
 * @param {{ children: import('react').ReactNode, startOrt?: { country: string, language: string } | null }} props
 *   `startOrt` einer Server-Seite (Next.js-Phase 2):
 *   Dort rendert der Provider zuerst auf dem Server, ohne `window` und ohne
 *   Cookies — Land und Sprache stehen dann aus der Adresse fest. Ohne
 *   `startOrt` liest er wie bisher Adresse, `?lang=` und Cookie.
 */
export function LanguageProvider({ children, startOrt = null }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  // State initialisieren aus URL oder Cookie
  const [country, setCountryState] = useState(() => {
    if (startOrt) return startOrt.country;
    const parsed = parseLocaleFromPath(window.location.pathname);
    return parsed.country || getCookie(COUNTRY_COOKIE) || DEFAULT_COUNTRY;
  });
  
  // Die Sprache steht schon beim ersten Render fest — sonst blitzt die falsche
  // auf. Reihenfolge: Praefix → `?lang=` eines Mail-Links → Cookie → Vorgabe.
  //
  // Die Sprache muss dabei NICHT zum Land passen. Bis 29.09.2026 wurde sie hier
  // (und in einem Effekt danach) auf die Vorgabe des Cookie-Landes gezwungen:
  // `/api/i18n/resolve` setzte fuer einen englischen Browser in Deutschland
  // `en` + `de`, und die flache `/reset-password` rendete deshalb Deutsch. Ob
  // eine Kombination als PRAEFIX gueltig ist, entscheidet erst der Weg in die
  // lokalisierte App (`landFuerSprache`).
  const [language, setLanguageState] = useState(() => {
    if (startOrt) return startOrt.language;
    const parsed = parseLocaleFromPath(window.location.pathname);
    if (parsed.language) return parsed.language;
    const ausMail = languageFromQuery(window.location.search);
    if (ausMail) return ausMail;
    const cookieLang = getCookie(LANGUAGE_COOKIE);
    return (cookieLang && SUPPORTED_LANGUAGES[cookieLang]) ? cookieLang : DEFAULT_LANGUAGE;
  });
  
  const ohneSprachCookieGestartet = useRef(startOrt ? false : !getCookie(LANGUAGE_COOKIE));

  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configVersion, setConfigVersion] = useState(0); // Force re-render when config loads

  // URL-Prefix für aktuelle Sprache
  const languagePrefix = getLangToUrlPrefix()[language] || 'deutsch';
  
  // Aktuelles Land-Objekt (dynamisch)
  const countries = dynamicCountries || DEFAULT_COUNTRIES;
  const currentCountry = countries[country] || countries[DEFAULT_COUNTRY] || DEFAULT_COUNTRIES[DEFAULT_COUNTRY];
  
  // Aktuelle Sprache-Objekt (dynamisch)
  const languages = dynamicLanguages || DEFAULT_LANGUAGES;
  const currentLanguage = languages[language] || languages[DEFAULT_LANGUAGE] || DEFAULT_LANGUAGES[DEFAULT_LANGUAGE];

  // Sprachen die für aktuelles Land verfügbar sind
  const languagesForCountry = (currentCountry?.languages || []).map(code => languages[code]).filter(Boolean);

  // =============================================================================
  // Backend-Daten laden
  // =============================================================================
  
  // `loadLocaleConfig` wirft nicht: Ein Fehlschlag steht in `configFehler` und
  // wird unten zu `anmeldeFreigabe.stand === 'fehler'`.
  const ladeLaender = useCallback(async () => {
    setLoading(true);
    await loadLocaleConfig();
    setConfigVersion(v => v + 1); // Trigger re-render mit neuer Config

    // Setze auch die alten States für Abwärtskompatibilität
    const langs = dynamicLanguages || DEFAULT_LANGUAGES;
    const ctries = dynamicCountries || DEFAULT_COUNTRIES;

    setAvailableLanguages(Object.values(langs));
    setAvailableCountries(Object.values(ctries));
    setLoading(false);
  }, []);

  useEffect(() => {
    ladeLaender();
  }, [ladeLaender]);

  // Anmeldung und Registrierung — offen NUR, wenn die Serverantwort dieser
  // Sitzung es für genau dieses Land sagt. Kein `!== false`: Ein fehlender
  // Wert heißt „weiß ich nicht", und „weiß ich nicht" öffnet keine
  // Registrierung. Die Schalter sind beim Laden geprüft (echte Booleans).
  // Neu gelesen wird bei jedem Render: `configVersion` stösst ihn an, sobald
  // sich die Modul-Variablen ändern.
  const anmeldeFreigabe = bestimmeAnmeldeFreigabe(country);

  // Die Länder, in denen eine Registrierung offen ist — Wahlliste für den
  // Wohnsitz (`useWohnsitzLaender`). Aus derselben Quelle wie die Freigabe
  // oben: der ERFOLGREICHEN Antwort dieser Sitzung, nie aus DEFAULT_COUNTRIES
  // (die tragen die Schalter nicht) und nie aus dem localStorage-Cache.
  const laenderMitRegistrierung = Object.values(serverLaender || {})
    .filter((land) => land.showRegister === true)
    .map((land) => String(land.code).toUpperCase());

  // Das Land im Zustand muss eines sein, das der Server anbietet — sonst gäbe es
  // für es keine Freigabe. Ein Cookie kann ein Land tragen, das inzwischen
  // abgeschaltet ist.
  useEffect(() => {
    if (!serverLaender || serverLaender[country] || !serverLaender[DEFAULT_COUNTRY]) return;
    console.warn(`Land ${country} wird nicht mehr angeboten, wechsle auf ${DEFAULT_COUNTRY}`);
    setCountryState(DEFAULT_COUNTRY);
    setCookie(COUNTRY_COOKIE, DEFAULT_COUNTRY);
  }, [country, configVersion]);

  // First-visit locale resolution (unified locale standard). On a GENUINE
  // first visit — no valid URL prefix AND no stored cookie — ask the backend
  // (/api/i18n/resolve: Accept-Language + GeoIP proxy header for anon,
  // User.language for authenticated) and redirect to that prefix instead of
  // the hardcoded "de" default. Returning visitors (cookie present) and
  // already-localized URLs are left untouched.
  useEffect(() => {
    if (parseLocaleFromPath(window.location.pathname).isValid) return;
    // Verwaltung und Mandanten-Übersicht sind global, ohne Sprach-Präfix.
    // Ein Erstbesuch dort (frischer Browser, Lesezeichen) lief sonst nach
    // /de/deutsch/admin — und von dort in den Auffangpfad nach /tables.
    if (istGlobalerPfad(window.location.pathname)) return;
    // „Erstbesuch" heisst: beim ERSTEN Render gab es kein Cookie. Nicht beim
    // Effekt gefragt — der Adress-Abgleich unten setzt das Cookie aus `?lang=`
    // schon im selben Durchlauf, und ein zweiter Effektlauf (StrictMode) hielte
    // den Besuch dann für einen wiederkehrenden.
    if (!ohneSprachCookieGestartet.current) return;
    let cancelled = false;
    (async () => {
      try {
        // Derselbe Kopf wie im API-Client: Ein Mail-Link (`?lang=it`) soll das
        // Cookie in der Sprache der Mail setzen, nicht in der des Browsers.
        const res = await fetch('/api/i18n/resolve', {
          credentials: 'include',
          cache: 'no-store',
          headers: localeHeaders(window.location.pathname, window.location.search),
        });
        if (!res.ok) {
          console.error(`/api/i18n/resolve antwortete ${res.status} — die Sprache bleibt bei ${DEFAULT_LANGUAGE}`);
          return;
        }
        if (cancelled) return;
        const data = await res.json();
        const lang = data.language;
        if (!SUPPORTED_LANGUAGES[lang]) {
          console.error(`/api/i18n/resolve lieferte die unbekannte Sprache ${lang}`);
          return;
        }
        const c = (data.country || '').toLowerCase();
        // Die Sprache gilt, auch wenn das Land sie nicht anbietet (englischer
        // Browser in Deutschland): Die flache Seite rendert in ihr. Das Land
        // behalten wir nur, wenn wir es kennen.
        setLanguageState(lang);
        setCookie(LANGUAGE_COOKIE, lang);
        if (SUPPORTED_COUNTRIES[c]) {
          setCountryState(c);
          setCookie(COUNTRY_COOKIE, c);
        }
        // Umgeleitet wird nur, was ohnehin ins Praefix gehoert: die Startseite
        // und eine Adresse, die inzwischen ein Praefix traegt. Eine flache
        // Seite (/reset-password, /verify-email, /invite/…) gibt es NUR flach —
        // ein Praefix davor fiel in die Auffang-Route, und ohne `search` ging
        // dabei auch das Token verloren.
        const { pathname, search, hash } = window.location;
        const parsed = parseLocaleFromPath(pathname);
        if (!parsed.isValid && pathname !== '/') return;
        const land = landFuerSprache(SUPPORTED_COUNTRIES[c] ? c : DEFAULT_COUNTRY, lang);
        navigate(buildLocalizedPath(land, lang, parsed.isValid ? parsed.rest : '/') + search + hash, { replace: true });
      } catch (err) {
        console.error('Erstbesuch: /api/i18n/resolve fehlgeschlagen — die Sprache bleibt bei der Vorgabe', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // =============================================================================
  // URL-Synchronisation
  // =============================================================================
  
  // Das Cookie folgt der ADRESSE, nicht nur einem Zustandswechsel: Beim ersten
  // Laden von `/fr/francais/...` steht die Sprache schon aus dem Pfad fest, der
  // Vergleich `parsed.language !== language` war also falsch, und ein altes
  // Cookie (`de`) blieb stehen. Das Backend liest genau dieses Cookie
  // (`locale_aus_anfrage`) — die Seite war französisch, die Fehlermeldung
  // darunter deutsch (auf Prod gemessen, 18.09.2026).
  //
  // Auf einer flachen Seite gilt dasselbe fuer `?lang=` aus einem Mail-Link:
  // Die Sprache folgt der Mail, das Cookie auch — sonst antwortet das Backend
  // beim naechsten Aufruf ohne den Parameter wieder in der alten Sprache. Das
  // Land bleibt, wie es ist (die Mail kennt es nicht).
  useEffect(() => {
    const parsed = parseLocaleFromPath(location.pathname);

    if (parsed.isValid) {
      if (parsed.country !== country) setCountryState(parsed.country);
      if (parsed.language !== language) setLanguageState(parsed.language);
      if (getCookie(COUNTRY_COOKIE) !== parsed.country) setCookie(COUNTRY_COOKIE, parsed.country);
      if (getCookie(LANGUAGE_COOKIE) !== parsed.language) setCookie(LANGUAGE_COOKIE, parsed.language);
      return;
    }
    const ausMail = languageFromQuery(location.search);
    if (ausMail) {
      if (ausMail !== language) setLanguageState(ausMail);
      if (getCookie(LANGUAGE_COOKIE) !== ausMail) setCookie(LANGUAGE_COOKIE, ausMail);
    }
  }, [location.pathname, location.search, country, language]);

  // =============================================================================
  // Setter-Funktionen
  // =============================================================================
  
  const setCountry = useCallback((newCountry) => {
    const countryLower = newCountry.toLowerCase();
    const countryConfig = SUPPORTED_COUNTRIES[countryLower];
    
    if (!countryConfig) {
      console.warn(`Unsupported country: ${newCountry}`);
      return;
    }
    
    // Prüfen ob aktuelle Sprache im neuen Land verfügbar ist
    let newLanguage = language;
    if (!countryConfig.languages.includes(language)) {
      newLanguage = countryConfig.defaultLanguage;
    }
    
    setCountryState(countryLower);
    setCookie(COUNTRY_COOKIE, countryLower);
    
    if (newLanguage !== language) {
      setLanguageState(newLanguage);
      setCookie(LANGUAGE_COOKIE, newLanguage);
    }
    
    // URL aktualisieren — nicht auf Seiten ohne Präfix (`globalePfade`).
    if (istGlobalerPfad(location.pathname)) return;
    const parsed = parseLocaleFromPath(location.pathname);
    // Der Pfad bleibt, auch die Wurzel: `/{land}/{sprache}` ist seit
    // 30.09.2026 die Startseite für Gäste. Vorher stand hier `/tables` als
    // Ersatz für die Wurzel — ein Gast, der auf der Startseite die Sprache
    // wechselte, landete bei der Anmeldung. Angemeldete schickt die Wurzel
    // ohnehin in die App (`LocalizedDefaultRedirect`).
    const restPath = parsed.rest || '/';
    const newPath = buildLocalizedPath(countryLower, newLanguage, restPath);
    navigate(newPath, { replace: true });
  }, [language, location.pathname, navigate]);

  const setLanguage = useCallback((newLanguage) => {
    const langLower = newLanguage.toLowerCase();
    
    if (!SUPPORTED_LANGUAGES[langLower]) {
      console.warn(`Unsupported language: ${newLanguage}`);
      return;
    }
    
    // Prüfen ob Sprache im aktuellen Land verfügbar ist
    if (!currentCountry.languages.includes(langLower)) {
      console.warn(`Language ${newLanguage} not available in ${country}`);
      return;
    }
    
    setLanguageState(langLower);
    setCookie(LANGUAGE_COOKIE, langLower);
    
    // URL aktualisieren — nicht auf Seiten ohne Präfix (`globalePfade`).
    if (istGlobalerPfad(location.pathname)) return;
    const parsed = parseLocaleFromPath(location.pathname);
    // Der Pfad bleibt, auch die Wurzel: `/{land}/{sprache}` ist seit
    // 30.09.2026 die Startseite für Gäste. Vorher stand hier `/tables` als
    // Ersatz für die Wurzel — ein Gast, der auf der Startseite die Sprache
    // wechselte, landete bei der Anmeldung. Angemeldete schickt die Wurzel
    // ohnehin in die App (`LocalizedDefaultRedirect`).
    const restPath = parsed.rest || '/';
    const newPath = buildLocalizedPath(country, langLower, restPath);
    navigate(newPath, { replace: true });
  }, [country, currentCountry, location.pathname, navigate]);

  // Combined setter: Land + Sprache in einem Schritt setzen (für LocaleSelector)
  const setLocale = useCallback((newCountry, newLanguage) => {
    const countryLower = newCountry.toLowerCase();
    const langLower = newLanguage.toLowerCase();
    const countryConfig = SUPPORTED_COUNTRIES[countryLower];
    if (!countryConfig) return;
    if (!SUPPORTED_LANGUAGES[langLower]) return;

    setCountryState(countryLower);
    setCookie(COUNTRY_COOKIE, countryLower);
    setLanguageState(langLower);
    setCookie(LANGUAGE_COOKIE, langLower);

    if (istGlobalerPfad(location.pathname)) return;
    const parsed = parseLocaleFromPath(location.pathname);
    // Der Pfad bleibt, auch die Wurzel: `/{land}/{sprache}` ist seit
    // 30.09.2026 die Startseite für Gäste. Vorher stand hier `/tables` als
    // Ersatz für die Wurzel — ein Gast, der auf der Startseite die Sprache
    // wechselte, landete bei der Anmeldung. Angemeldete schickt die Wurzel
    // ohnehin in die App (`LocalizedDefaultRedirect`).
    const restPath = parsed.rest || '/';
    const newPath = buildLocalizedPath(countryLower, langLower, restPath);
    navigate(newPath, { replace: true });
  }, [location.pathname, navigate]);

  // =============================================================================
  // Hilfsfunktionen
  // =============================================================================
  
  // Ein Praefix ist nur gueltig, wenn das Land die Sprache fuehrt. Seit die
  // Sprache auf flachen Seiten der Mail folgen darf (`?lang=`), kann der
  // Zustand `de` + `en` sein; `/de/english` fiele in `ValidateLocalePrefix`
  // zurueck auf Deutsch. `landFuerSprache` haelt die Sprache und nimmt ihr
  // Heimatland.
  const getLocalizedPath = useCallback((path) => {
    return buildLocalizedPath(landFuerSprache(country, language), language, path);
  }, [country, language]);

  const getPathWithoutLocale = useCallback((pathname = location.pathname) => {
    const parsed = parseLocaleFromPath(pathname);
    return parsed.rest || '/';
  }, [location.pathname]);

  // =============================================================================
  // Context Value
  // =============================================================================
  
  const value = {
    // Current state
    country,
    language,
    languagePrefix,
    
    // Objects
    currentCountry,
    currentLanguage,
    
    // Available options
    languagesForCountry,
    availableLanguages,
    availableCountries,
    supportedLanguages: SUPPORTED_LANGUAGES,
    supportedCountries: SUPPORTED_COUNTRIES,
    
    // Setters
    setCountry,
    setLanguage,
    setLocale,
    
    // Helpers
    getLocalizedPath,
    getPathWithoutLocale,
    
    // Compliance-Schalter des aktuellen Landes (siehe `anmeldeFreigabe` oben).
    // Gelesen über `useAnmeldeFreigabe`, das auch die Plattform-Schalter kennt.
    anmeldeFreigabe,
    laenderMitRegistrierung,
    ladeLaenderNeu: ladeLaender,

    // State
    loading,
    isDefaultCountry: country === DEFAULT_COUNTRY,
    isDefaultLanguage: language === DEFAULT_LANGUAGE,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    console.warn('useLanguage used outside LanguageProvider, using defaults');
    return {
      country: DEFAULT_COUNTRY,
      language: DEFAULT_LANGUAGE,
      languagePrefix: 'deutsch',
      currentCountry: SUPPORTED_COUNTRIES[DEFAULT_COUNTRY],
      currentLanguage: SUPPORTED_LANGUAGES[DEFAULT_LANGUAGE],
      languagesForCountry: [SUPPORTED_LANGUAGES[DEFAULT_LANGUAGE]],
      availableLanguages: [],
      availableCountries: [],
      supportedLanguages: SUPPORTED_LANGUAGES,
      supportedCountries: SUPPORTED_COUNTRIES,
      setCountry: () => {},
      setLanguage: () => {},
      setLocale: () => {},
      getLocalizedPath: (path) => `/de/deutsch${path.startsWith('/') ? path : '/' + path}`,
      getPathWithoutLocale: () => '/',
      // Ohne Provider gibt es keine Serverantwort — also keine Freigabe.
      anmeldeFreigabe: { stand: 'fehler', login: false, registrierung: false },
      laenderMitRegistrierung: [],
      ladeLaenderNeu: () => {},
      loading: false,
      isDefaultCountry: true,
      isDefaultLanguage: true,
    };
  }
  return context;
}

// Exports
export default LanguageContext;
